import {
  BUDGET,
  WORKS_BUDGET,
  dLong,
  fmt,
  pct1,
  type Engine,
  type CatPcts,
  type FinPcts,
  type SpentBy,
  type TimeInfo,
} from '@piaip/shared';

export interface SchemeStatus {
  cats: CatPcts;
  spent: SpentBy;
  fin: FinPcts;
  t: TimeInfo;
  /** Physical progress less time elapsed, in percentage points. */
  gap: number;
  color: string;
  bg: string;
  border: string;
  head: string;
  text: string;
}

/**
 * The single verdict on where the scheme stands, in plain language.
 *
 * The dashboard banner, the executive summary and the delay report all show the
 * same sentence, so it is written once here.
 */
export function schemeStatus(engine: Engine): SchemeStatus {
  const cats = engine.catPcts();
  const spent = engine.spentBy();
  const fin = engine.finPcts();
  const t = engine.timeInfo();
  const gap = cats.overall - t.pct;
  const behind = gap < -2;

  return {
    cats,
    spent,
    fin,
    t,
    gap,
    color: behind ? '#a3372c' : gap < 0 ? '#b06a12' : '#2f7d51',
    bg: behind ? '#fbeae7' : gap < 0 ? '#fdf3e3' : '#eaf5ee',
    border: behind ? '#f0cfc9' : gap < 0 ? '#f2e0bd' : '#cbe6d5',
    head: behind ? 'Behind schedule' : gap < 0 ? 'Slightly behind schedule' : 'On or ahead of schedule',
    text:
      `${t.elapsed} of the ${t.total} days allowed for the scheme have passed — that is ${pct1(t.pct)}% of the time. ` +
      `${pct1(cats.overall)}% of the construction work is finished, a difference of ${pct1(Math.abs(gap))} percentage points ` +
      `${gap < 0 ? 'against' : 'in favour of'} the scheme. ` +
      `Rs ${fmt(fin.works, 1)} million of the Rs ${fmt(WORKS_BUDGET, 1)} million construction budget has been paid ` +
      `(${pct1(fin.worksPct)}%); with land, environment, contingencies and tax, Rs ${fmt(spent.total, 1)} million ` +
      `of the sanctioned Rs ${fmt(BUDGET, 1)} million has gone out, leaving Rs ${fmt(BUDGET - spent.total, 1)} million. ` +
      `${t.remaining} days remain until ${dLong('2028-12-31')}.`,
  };
}
